import type {
  AuthProvidersDto,
  AuthSessionDto,
  CompleteSocialSignupDto,
  CreateInvitationsInput,
  EmailPreferencesDto,
  EmailPreferencesUpdateDto,
  FriendRelationDto,
  FriendSearchDto,
  FriendsListDto,
  GriffonneConfigInput,
  GuestSessionDto,
  InvitationResultDto,
  LinkedIdentityDto,
  LoginDto,
  MotusDailyGuessResult,
  MotusDailyStats,
  MotusDailyToday,
  MotusDefinition,
  MotusDictionaryCheck,
  PandaPanicTicket,
  PrivateProfileDto,
  ProfileStatsDto,
  PublicProfileDto,
  PublicUserDto,
  PushConfigDto,
  PushDeviceDto,
  PushPreferencesDto,
  PushPreferencesUpdateDto,
  PushRegisterDto,
  SentInvitationDto,
  SignupDto,
  SocialPendingDto,
  SocialProviderName,
  UpdateProfileDto,
} from '@redpandev/shared';

import { csrfHeaders, sessionGate } from './session-gate';

/**
 * Client HTTP du front. Cookies inclus, CSRF double-submit. La FRAÎCHEUR DE
 * SESSION (401 → refresh single-flight → rejeu transparent, y compris les 401
 * tardifs) est déléguée à la barrière `session-gate.ts` (ADR 0016, spec 01
 * §5.4) — aucun appelant ne gère lui-même un refresh.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    /** Corps d'erreur JSON complet (ex : `retryAfter` d'un 429) — jamais rendu brut. */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

/** Partie en cours où je suis JOUEUR (hôte ou joueur admis), pour la page profil. */
export interface ActiveMatch {
  matchId: string;
  game: string;
  joinCode: string;
  status: string;
  opponent: { userId?: string; displayName: string } | null;
  createdAt: string;
}

// Endpoints qui NE passent PAS par la barrière : pas encore de session (login/
// signup, onboarding social) ou refresh lui-même — jamais de refresh+rejeu
// automatiques ici (un 401 « ticket/mot de passe » ne doit pas tenter un refresh).
const NO_REFRESH = new Set([
  '/auth/refresh',
  '/auth/login',
  '/auth/signup',
  '/auth/social/pending',
  '/auth/social/complete',
  '/auth/social/link/confirm',
]);

function raw(method: string, path: string, body?: unknown): Promise<Response> {
  // En-têtes calculés À CHAQUE envoi (un rejeu post-refresh relit le cookie CSRF).
  const send = (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const mutating = method !== 'GET' && method !== 'HEAD';
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (mutating) Object.assign(headers, csrfHeaders());
    return fetch('/api' + path, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };
  return NO_REFRESH.has(path) ? send() : sessionGate.run(send);
}

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await raw(method, path, body);
  const data = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    const body = data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
    const code = (body && 'error' in body && String(body.error)) || `http_${res.status}`;
    throw new ApiError(res.status, code, undefined, body);
  }
  return data as T;
}

export const api = {
  me: () => json<PublicUserDto>('GET', '/auth/me'),
  signup: (dto: SignupDto) => json<AuthSessionDto>('POST', '/auth/signup', dto),
  login: (dto: LoginDto) => json<AuthSessionDto>('POST', '/auth/login', dto),
  logout: () => json<void>('POST', '/auth/logout'),
  profileMe: () => json<PrivateProfileDto>('GET', '/profile/me'),
  myStats: () => json<ProfileStatsDto>('GET', '/profile/me/stats'),
  updateProfile: (dto: UpdateProfileDto) => json<PrivateProfileDto>('PATCH', '/profile/me', dto),
  publicProfile: (displayName: string) =>
    json<PublicProfileDto>('GET', `/profile/${encodeURIComponent(displayName)}`),
  checkDisplayName: (displayName: string) =>
    json<{ available: boolean }>(
      'GET',
      `/profile/check?displayName=${encodeURIComponent(displayName)}`,
    ),
  // Parties en cours où je suis joueur (hôte/joueur admis) — affichées dans le profil.
  activeMatches: () => json<{ matches: ActiveMatch[] }>('GET', '/profile/me/active-matches'),

  // --- Amis (spec 09, ADR 0019 — amitié MUTUELLE : demande → acceptation) ---
  // Amis acceptés + demandes reçues + demandes envoyées (3 sections).
  friends: () => json<FriendsListDto>('GET', '/friends'),
  // Demande d'ami. Réponse 200 UNIFORME `{ relation }` — un no-op silencieux
  // (refus caché, anti-spam) est indistinguable d'un envoi : le front ne présume
  // JAMAIS d'un état au-delà de la relation renvoyée.
  requestFriend: (userId: string) =>
    json<FriendRelationDto>('POST', '/friends/requests', { userId }),
  // Accepte la demande reçue de `userId` → { relation: 'friend' }.
  // Erreurs : request_not_found (404), friends_limit_reached (422).
  acceptFriend: (userId: string) =>
    json<FriendRelationDto>('POST', `/friends/requests/${encodeURIComponent(userId)}/accept`),
  // Refuse (soft, invisible pour le demandeur) → { relation: 'none' }.
  declineFriend: (userId: string) =>
    json<FriendRelationDto>('POST', `/friends/requests/${encodeURIComponent(userId)}/decline`),
  // Retire un ami OU annule MA demande (204, idempotent).
  removeFriend: (userId: string) => json<void>('DELETE', `/friends/${encodeURIComponent(userId)}`),
  searchFriends: (q: string) =>
    json<FriendSearchDto>('GET', `/friends/search?q=${encodeURIComponent(q)}`),

  // --- Matchmaking (socle temps réel) ---
  createMatch: (game: string, metadata?: Record<string, unknown>) =>
    json<{ matchId: string; joinCode: string; joinGrant: string }>(
      'POST',
      `/games/${game}/matches`,
      {
        metadata,
      },
    ),
  // Accepte un matchId OU un joinCode court ; renvoie toujours l'ID canonique du match.
  joinMatch: (game: string, idOrCode: string) =>
    json<{ matchId: string; joinGrant: string }>(
      'POST',
      `/games/${game}/matches/${encodeURIComponent(idOrCode)}/join`,
      {},
    ),
  matchSummary: (matchId: string) =>
    json<{
      id: string;
      game: string;
      status: string;
      joinCode: string;
      players: { userId: string; displayName: string }[];
    }>('GET', `/matches/${encodeURIComponent(matchId)}`),
  motusCheckWord: (word: string, length: number) =>
    json<MotusDictionaryCheck>(
      'GET',
      `/motus/dictionary/check?word=${encodeURIComponent(word)}&length=${length}`,
    ),
  // Définition FR d'un mot RÉVÉLÉ (spec §3.5, authentifié). `definition:null` = mot
  // inconnu du lexique. N'est JAMAIS appelé avec un mot secret non révélé (anti-triche).
  motusDefinition: (word: string) =>
    json<MotusDefinition>('GET', `/motus/dictionary/definition?word=${encodeURIComponent(word)}`),
  // `length` optionnelle : absente = « peu importe » (le serveur tire toutes longueurs).
  motusRandomWord: (length?: number) =>
    json<{ word: string }>(
      'GET',
      `/motus/dictionary/random${length != null ? `?length=${length}` : ''}`,
    ),
  // Jusqu'à 3 mots valides proches du brouillon tapé (aide chooser, neutre anti-triche).
  motusSuggestWords: (word: string) =>
    json<{ suggestions: string[] }>(
      'GET',
      `/motus/dictionary/suggest?word=${encodeURIComponent(word)}`,
    ),

  // --- Identité de session (ADR 0013) : compte OU invité par cookie signé ---
  // AUTH-AWARE : { kind:'user' } si connecté, sinon { kind:'guest', displayName }.
  // À appeler AVANT d'ouvrir un socket en anonyme (pose rp_guest + rp_csrf) — le
  // handshake exige le cookie `rp_guest` présent (spec games/griffonne §5).
  guestMe: () => json<GuestSessionDto>('GET', '/guest/me'),

  // --- Griffonne (dessine & devine — amorçage REST, gameplay via socket §9) ---
  // « Rejoindre la partie commune » : un salon public non plein (ou en crée un).
  griffonneMatchmake: () => json<{ matchId: string }>('POST', '/griffonne/matchmake'),
  // Crée une partie PRIVÉE configurable (l'auteur en est l'hôte) → code partageable.
  griffonneCreate: (config: GriffonneConfigInput) =>
    json<{ matchId: string; joinCode: string }>('POST', '/griffonne/matches', config),
  // Rejoint une partie par matchId OU joinCode (privé partagé) → ID canonique.
  griffonneJoin: (idOrCode: string) =>
    json<{ matchId: string }>(
      'POST',
      `/griffonne/matches/${encodeURIComponent(idOrCode)}/join`,
      {},
    ),

  // --- Panda Panic en ligne (spec games/panda-panic §12) — amorçage REST du jeu 3D ---
  // Demande un ticket d'accès à une salle du serveur autoritaire (Colyseus). AUTH-AWARE
  // (compte OU invité — jouable sans compte) : appeler `guestMe()` d'abord pose
  // rp_guest + rp_csrf. `room` absent → l'API GÉNÈRE le code (création) ; présent →
  // rejoint ce code. Réponse : { token (JWT court présenté au join), room, wsUrl, ttlSeconds }.
  // 404 si le flag PANDA_PANIC_ONLINE est off. Le gameplay passe ensuite par le WS, jamais l'API.
  // `seats` (création seule, 2–4) : nombre total de sièges — les vides = bots au lancement.
  pandaPanicTicket: (room?: string, seats?: number) =>
    json<PandaPanicTicket>('POST', '/panda-panic/tickets', {
      ...(room ? { room } : {}),
      ...(seats !== undefined ? { seats } : {}),
    }),

  // --- Motus du jour (spec games/motus-daily §6 — REST pur, ADR 0011) ---
  // Mot du jour + partie du viewer. `answer` n'y figure QUE si SA partie est finie.
  motusDailyToday: () => json<MotusDailyToday>('GET', '/motus-daily/today'),
  // Une proposition. 422 invalid_guess (non consommée) · 409 play_finished / day_over · 429.
  motusDailyGuess: (dayKey: string, word: string) =>
    json<MotusDailyGuessResult>('POST', '/motus-daily/guess', { dayKey, word }),
  // Répartition d'un jour (défaut : aujourd'hui). Le front ne l'appelle qu'une fois
  // la partie du viewer terminée (spec §9 — pas de spoil de difficulté avant de jouer).
  motusDailyStats: (day?: string) =>
    json<MotusDailyStats>(
      'GET',
      `/motus-daily/stats${day ? `?day=${encodeURIComponent(day)}` : ''}`,
    ),

  // --- Emails transactionnels & préférences (spec 06) ---
  // Renvoi de l'email de vérification (session). 202 neutre ; 404 si la feature est off.
  requestEmailVerification: () => json<void>('POST', '/auth/email/verify/request'),
  // Confirmation du lien reçu par email (public). 400 invalid_token / 410 token_expired.
  confirmEmailVerification: (token: string) =>
    json<{ verified: boolean }>('POST', '/auth/email/verify/confirm', { token }),
  // Zone dangereuse (spec 07 §3.5/§3.6) : révoque toutes les AUTRES sessions (la courante survit).
  revokeOtherSessions: () => json<{ revoked: number }>('POST', '/auth/sessions/revoke-others'),
  // Suppression du compte par anonymisation (re-preuve DUALE, spec 11 §7). Sans
  // argument = compte social-only : la preuve `rp_reauth` (cookie) voyage seule.
  // Cookies purgés par le serveur.
  deleteAccount: (currentPassword?: string) =>
    json<void>('POST', '/auth/account/delete', currentPassword ? { currentPassword } : {}),
  // « Mot de passe oublié » (public). 202 NEUTRE systématique ; 404 si la feature est off.
  requestPasswordReset: (email: string) => json<void>('POST', '/auth/password/forgot', { email }),
  // « Définir un mot de passe » a posteriori (spec 11 §7, session) : envoie un lien
  // email qui CRÉE l'identité locale d'un compte social-only. 202 ; 404 si feature off.
  requestPasswordSet: () => json<void>('POST', '/auth/password/set/request'),
  // Reset par le token reçu par email (public). 400 invalid_token / 410 token_expired.
  confirmPasswordReset: (token: string, password: string) =>
    json<{ reset: boolean }>('POST', '/auth/password/reset', { token, password }),
  // Désinscription des emails non essentiels via token signé (public, fonctionne déconnecté).
  unsubscribeEmails: (token: string) =>
    json<{ optedOut: boolean }>('POST', '/email/unsubscribe', { token }),
  // Undo de la désinscription (public, même token signé).
  resubscribeEmails: (token: string) =>
    json<{ optedOut: boolean }>('POST', '/email/resubscribe', { token }),
  // Préférences email du compte connecté. 404 si la feature est off.
  getEmailPreferences: () => json<EmailPreferencesDto>('GET', '/email/preferences/me'),
  // PATCH partiel (spec 10 §5) : `{ optedOut? }` et/ou `{ invitesOptedOut? }`.
  updateEmailPreferences: (prefs: EmailPreferencesUpdateDto) =>
    json<EmailPreferencesDto>('PATCH', '/email/preferences/me', prefs),

  // --- Invitations à jouer (spec 10) — lobby privé → email ---
  // Invite des amis (`friendIds`) et/ou des emails externes. 200 = traitement de
  // lot : chaque cible reçoit son statut ('sent' | 'already_invited'). ⚠️ `sent`
  // = invitation ENREGISTRÉE — jamais d'info sur l'envoi effectif (anti-énumération).
  createInvitations: (game: string, idOrCode: string, dto: CreateInvitationsInput) =>
    json<{ invitations: InvitationResultDto[] }>(
      'POST',
      `/games/${encodeURIComponent(game)}/matches/${encodeURIComponent(idOrCode)}/invitations`,
      dto,
    ),
  // MES invitations émises sur cette partie (état « Invité ✓ » persistant du lobby).
  listInvitations: (game: string, idOrCode: string) =>
    json<{ invitations: SentInvitationDto[] }>(
      'GET',
      `/games/${encodeURIComponent(game)}/matches/${encodeURIComponent(idOrCode)}/invitations`,
    ),
  // Suppression persistante des emails d'invitation (public, token signé, spec 10 §6).
  suppressInviteEmails: (token: string) =>
    json<{ suppressed: boolean }>('POST', '/email/invites/suppress', { token }),
  // Undo de la suppression (public, même token signé).
  unsuppressInviteEmails: (token: string) =>
    json<{ suppressed: boolean }>('POST', '/email/invites/unsuppress', { token }),

  // --- Notifications push (spec 13, ADR 0021 — FCM) ---
  // Config web Firebase PUBLIQUE + clé VAPID. @Public ; 404 si la feature est off
  // (sonde de `usePushFeature`). Le token FCM n'y figure JAMAIS (secret appareil).
  getPushConfig: () => json<PushConfigDto>('GET', '/push/config'),
  // Upsert idempotent de CET appareil (enregistrement + revalidation, spec 13 §5).
  registerPushDevice: (dto: PushRegisterDto) => json<PushDeviceDto>('POST', '/push/devices', dto),
  // Mes appareils abonnés (jamais le token — seulement `tokenHash`).
  listPushDevices: () => json<PushDeviceDto[]>('GET', '/push/devices'),
  // Révoque un appareil (ownership → 404 si pas le mien, pas d'oracle).
  deletePushDevice: (id: string) => json<void>('DELETE', `/push/devices/${encodeURIComponent(id)}`),
  // Préférences push exprimées en « recevoir » (l'API stocke l'inverse, opt-out).
  getPushPreferences: () => json<PushPreferencesDto>('GET', '/push/preferences/me'),
  updatePushPreferences: (prefs: PushPreferencesUpdateDto) =>
    json<PushPreferencesDto>('PATCH', '/push/preferences/me', prefs),

  // --- Gestion du compte (spec 07 §3) ---
  // Changement de mot de passe (re-preuve du mot de passe courant). 200 → nouvelle
  // session (cookies ré-émis par l'API) ; 401 invalid_credentials ; 429 lockout.
  changePassword: (currentPassword: string, newPassword: string) =>
    json<AuthSessionDto>('POST', '/auth/password/change', { currentPassword, newPassword }),
  // Changement d'email : l'ancienne adresse reste active tant que la nouvelle
  // n'est pas vérifiée. 202 {pendingEmail} ; 400 same_email ; 401 invalid_credentials ;
  // 409 email_taken ; 404 si la feature email est off (l'UI masque le bloc).
  // Re-preuve DUALE (spec 11 §7) : `currentPassword` pour un compte à mot de passe,
  // sinon rien (compte social-only → preuve `rp_reauth` en cookie).
  changeEmail: (newEmail: string, currentPassword?: string) =>
    json<{ pendingEmail: string }>(
      'POST',
      '/auth/email/change',
      currentPassword ? { newEmail, currentPassword } : { newEmail },
    ),
  // Annule le changement d'email en attente (pendingEmail remis à null). 204.
  cancelEmailChange: () => json<void>('POST', '/auth/email/change/cancel'),

  // --- Connexion sociale (spec 11, ADR 0020 — OIDC en direct) ---
  // Providers sociaux ACTIFS (sonde des boutons login/signup). `[]` si tous off
  // (feature dark) → aucun bouton rendu. Le DÉPART OAuth n'est PAS un fetch : c'est
  // une navigation plein écran vers `/api/auth/:provider/start` (redirect provider).
  getSocialProviders: () => json<AuthProvidersDto>('GET', '/auth/providers'),
  // Données de préremplissage de l'onboarding (lues du cookie ticket `rp_signup`).
  // 401 signup_ticket_invalid si le ticket a expiré → retour au bouton social.
  getSocialPending: () => json<SocialPendingDto>('GET', '/auth/social/pending'),
  // Finalise le signup social (pseudo choisi) → session (cookies posés par l'API).
  // 409 email_taken / displayname_taken ; 401 signup_ticket_invalid.
  completeSocialSignup: (dto: CompleteSocialSignupDto) =>
    json<AuthSessionDto>('POST', '/auth/social/complete', dto),
  // Confirme une liaison cas (c) : preuve du mot de passe → session. 401
  // invalid_credentials / link_confirmation_required ; 429 lockout.
  confirmSocialLink: (currentPassword: string) =>
    json<AuthSessionDto>('POST', '/auth/social/link/confirm', { currentPassword }),
  // Identités sociales liées (réglages « Comptes connectés », spec 11 §6). Session
  // requise ; `[]` si aucun lien (en prod, feature dark). Jamais le `sub` provider.
  listIdentities: () => json<LinkedIdentityDto[]>('GET', '/auth/identities'),
  // Délie une identité sociale (session + CSRF). 204 ; 404 si non liée ; 409
  // last_login_method si c'est le dernier moyen de connexion (garde-fou §6).
  unlinkIdentity: (provider: SocialProviderName) =>
    json<void>('DELETE', `/auth/identities/${encodeURIComponent(provider)}`),
};
